import { DataResponse } from 'services/types';
import { CHECKOUT_POSTMESSAGE_SOURCE } from '../constants';

export type SubscriptionFeatureConfig = {
  /**
   * flag to determine if this feature is enabled for the subscription
   */
  enabled?: boolean;
};

export type SubscriptionFeature = {
  /**
   * animating images allowed
   */
  animateImage: SubscriptionFeatureConfig;
  /**
   * back catalog automation allowed
   */
  backCatalogAutomation: SubscriptionFeatureConfig;
  /**
   * add custom fonts allowed
   */
  customFont: SubscriptionFeatureConfig;
  /**
   * save custom template allowed
   */
  customTemplate: SubscriptionFeatureConfig;
  /**
   * adding custom watermarks allowed
   */
  customWatermark: SubscriptionFeatureConfig;
  /**
   * display recommendation widget in the exporting screen
   */
  exportScreenRecWidget: SubscriptionFeatureConfig;
  /**
   * eddy transcript automation allowed
   */
  eddyTranscriptAutomation: SubscriptionFeatureConfig;
  /**
   * full episode project creation and video export allowed with captions
   */
  fullEpisodeCaption: SubscriptionFeatureConfig;
  /**
   * setting full episode video export size allowed
   */
  fullEpisodeExportSize: SubscriptionFeatureConfig;
  /**
   * requesting full episode transcript allowed
   */
  fullEpisodeTranscript: SubscriptionFeatureConfig;
  /**
   * requesting full episode video allowed
   */
  fullEpisodeVideo: SubscriptionFeatureConfig;
  /**
   * multiple automations allowed
   */
  multipleAutomation: SubscriptionFeatureConfig;
  /**
   * generating text to video allowed
   */
  textToVideo: SubscriptionFeatureConfig;
  /**
   * styling captions in the UCS allowed
   */
  ucsCaptionStyling: SubscriptionFeatureConfig;
  /**
   * restore embed video allowed
   */
  restoreEmbedVideo: SubscriptionFeatureConfig;
  /**
   * clip suggestions bulk export allowed
   */
  clipSuggestionsBulkExport: SubscriptionFeatureConfig;
  /**
   * social post captions allowed
   */
  socialPostCaptions: SubscriptionFeatureConfig;
  /**
   * scheduled social post allowed
   */
  scheduledSocialPost: SubscriptionFeatureConfig;
  /**
   * youtube most replayed allowed
   */
  youtubeMostReplayed: SubscriptionFeatureConfig;
};

export type SubscriptionPauseStatus = 'queued' | 'active';

export type SubscriptionPauseSchedule = {
  /**
   * seconds since epoch when this plan is or will be un-paused
   */
  pauseEnd?: number;
  /**
   * seconds since epoch when this plan is or will be paused
   */
  pauseStart?: number;
  /**
   * pause status - queued or active
   */
  pauseStatus?: SubscriptionPauseStatus;
};

export type PlanLimit = {
  /**
   * max media duration limit in hours for eddy
   */
  eddyDurationLimitHours?: number;
  /**
   * maximum eddy upload size (MB)
   */
  eddyUploadMB?: number;
  /**
   * max audio duration limit in hours for full episode
   */
  fullEpisodeDurationLimitHours?: number;
  /**
   * maximum video upload size (MB)
   */
  videoUploadMB?: number;
};

export type PlanFeatureUsage = {
  /**
   * total allowed within the period
   */
  limit?: number;
  /**
   * flag to indicate if overage is reached
   */
  overageReached?: boolean;
  /**
   * end of current usage period in milliseconds since epoch
   */
  periodEndMillis?: number;
  /**
   * start of current usage period in milliseconds since epoch
   */
  periodStartMillis?: number;
  /**
   * total remaining within the period
   */
  remaining?: number;
  /**
   * total used within the period
   */ used?: number;
};

export type PlanUsage = {
  /**
   * in milliseconds
   */
  eddyTranscriptDuration: PlanFeatureUsage;
  /**
   * general overage reach indication. this will be a combination of various usage
   * signals. currently only supports video export overage
   */
  overageReached?: boolean;
  /**
   * in milliseconds
   */
  transcriptDuration: PlanFeatureUsage;
  /**
   * in milliseconds
   */
  videoTranscriptDuration: PlanFeatureUsage;
  videoExport: PlanFeatureUsage;
};

export type PurchaseManagementPlatform =
  | 'stripe'
  | 'itunesAppStore'
  | 'playStore';

export type PurchaseManagement = {
  /**
   * platform where the subscription was purchased. in order to make any updates
   * to the subscription, the original platform that was used to purchase the
   * subscription must be used. i.e., if plan was purchased using itunes app
   * store, the client must ensure that any updates to the subscription is performed
   * on itunes app store. Cross platform subscription management is not supported
   * and will return an error.
   */
  platform?: PurchaseManagementPlatform;
};

export enum SubscriptionPlanName {
  BASIC_20200922_MONTHLY = 'basic20200922Monthly',
  BASIC_20200922_YEARLY = 'basic20200922Yearly',
  BASIC_20211112_MONTHLY = 'basic20211112Monthly',
  BASIC_20211112_YEARLY = 'basic20211112Yearly',
  BASIC_20250516_MONTHLY = 'basic20250516Monthly',
  BASIC_20250516_YEARLY = 'basic20250516Yearly',
  BASIC_20250818_MONTHLY = 'basic20250818Monthly',
  BASIC_20250818_YEARLY = 'basic20250818Yearly',
  BASIC_EDUCATION_CHARITY_FOREVER = 'basicEducationCharityForever',
  BASIC_PAUSED_MONTHLY = 'basicPausedMonthly',
  BASIC_PAUSED_YEARLY = 'basicPausedYearly',
  EDUCATION_NON_PROFIT_FOREVER = 'educationNonprofitForever',
  ENTERPRISE_API_INTEGRATOR_FOREVER = 'enterpriseApiIntegratorForever',
  ENTERPRISE_FOREVER = 'enterpriseForever',
  FREE_FOREVER = 'freeForever',
  PRO_20200922_MONTHLY = 'pro20200922Monthly',
  PRO_20200922_YEARLY = 'pro20200922Yearly',
  PRO_20211112_MONTHLY = 'pro20211112Monthly',
  PRO_20211112_YEARLY = 'pro20211112Yearly',
  PRO_20250516_MONTHLY = 'pro20250516Monthly',
  PRO_20250516_YEARLY = 'pro20250516Yearly',
  PRO_20250818_MONTHLY = 'pro20250818Monthly',
  PRO_20250818_YEARLY = 'pro20250818Yearly',
  PRO_GROW_20251120_MONTHLY = 'progrow20251120Monthly',
  PRO_GROW_20251120_YEARLY = 'progrow20251120Yearly',
  PRO_ADMIN_ASSIGNED_MANUAL = 'proAdminAssignedManual',
  PRO_EDUCATION_CHARITY_FOREVER = 'proEducationCharityForever',
  PRO_FREE_TEMPORARY_MONTHLY = 'proFreeTemporaryMonthly',
  PRO_FREE_TEMPORARY_YEARLY = 'proFreeTemporaryYearly',
  PRO_MONTHLY = 'proMonthly',
  PRO_PAUSED_MONTHLY = 'proPausedMonthly',
  PRO_PAUSED_YEARLY = 'proPausedYearly',
  PRO_TRIAL_MANUAL = 'proTrialManual',
  PRO_TRIAL_PROMOTION_ACCEPT = 'proTrialPromotionAccept',
  PRO_TRIAL_REFERRAL_ACCEPT = 'proTrialReferralAccept',
  PRO_TRIAL_REFERRAL_OWNER = 'proTrialReferralOwner',
  PRO_YEARLY = 'proYearly',
  UNLIMITED_20211112_MONTHLY = 'unlimited20211112Monthly',
  UNLIMITED_20211112_YEARLY = 'unlimited20211112Yearly',
  UNLIMITED_ADMIN_ASSIGNED_MANUAL = 'unlimitedAdminAssignedManual',
}

export enum TargetPlanAlias {
  BASIC_MONTHLY_LATEST = 'basicMonthlyLatest',
  BASIC_YEARLY_LATEST = 'basicYearlyLatest',
  PRO_MONTHLY_LATEST = 'proMonthlyLatest',
  PRO_YEARLY_LATEST = 'proYearlyLatest',
  PRO_GROW_MONTHLY_LATEST = 'progrowMonthlyLatest',
  PRO_GROW_YEARLY_LATEST = 'progrowYearlyLatest',
  UNLIMITED_MONTHLY_LATEST = 'unlimitedMonthlyLatest',
  UNLIMITED_YEARLY_LATEST = 'unlimitedYearlyLatest',
}

export type SubscribablePlanName =
  | SubscriptionPlanName.BASIC_20200922_MONTHLY
  | SubscriptionPlanName.BASIC_20200922_YEARLY
  | SubscriptionPlanName.BASIC_20211112_MONTHLY
  | SubscriptionPlanName.BASIC_20211112_YEARLY
  | SubscriptionPlanName.BASIC_20250516_MONTHLY
  | SubscriptionPlanName.BASIC_20250516_YEARLY
  | SubscriptionPlanName.BASIC_20250818_MONTHLY
  | SubscriptionPlanName.BASIC_20250818_YEARLY
  | SubscriptionPlanName.FREE_FOREVER
  | SubscriptionPlanName.PRO_20200922_MONTHLY
  | SubscriptionPlanName.PRO_20200922_YEARLY
  | SubscriptionPlanName.PRO_20211112_MONTHLY
  | SubscriptionPlanName.PRO_20211112_YEARLY
  | SubscriptionPlanName.PRO_20250516_MONTHLY
  | SubscriptionPlanName.PRO_20250516_YEARLY
  | SubscriptionPlanName.PRO_20250818_MONTHLY
  | SubscriptionPlanName.PRO_20250818_YEARLY
  | SubscriptionPlanName.PRO_GROW_20251120_MONTHLY
  | SubscriptionPlanName.PRO_GROW_20251120_YEARLY
  | SubscriptionPlanName.PRO_FREE_TEMPORARY_MONTHLY
  | SubscriptionPlanName.PRO_FREE_TEMPORARY_YEARLY
  | SubscriptionPlanName.PRO_MONTHLY
  | SubscriptionPlanName.PRO_YEARLY
  | SubscriptionPlanName.UNLIMITED_20211112_MONTHLY
  | SubscriptionPlanName.UNLIMITED_20211112_YEARLY;

export type MonthlyPlanName = {
  [K in SubscribablePlanName as string]: K extends `${string}Monthly`
    ? K
    : never;
}[string];

export type YearlyPlanName = {
  [K in SubscribablePlanName as string]: K extends `${string}Yearly`
    ? K
    : never;
}[string];

export type SubscriptionPlan = {
  /**
   * takes form of cuid
   */
  id: string;
  name: SubscriptionPlanName;
};

export type SubscriptionPlanDetail = SubscriptionPlan & {
  /**
   * value in cents for USD
   */
  amount?: number;
  currenty?: string;
};

export type PlanAssociation = {
  displayName: string;
};

export enum ProductSubTier {
  BASIC_20200922_PAID = 'basic20200922Paid',
  BASIC_20211112_PAID = 'basic20211112Paid',
  BASIC_20250516_PAID = 'basic20250516Paid',
  BASIC_20250818_PAID = 'basic20250818Paid',
  BASIC_EDUCATION_CHARITY = 'basicEducationCharity',
  BASIC_PAUSED = 'basicPaused',
  PRO_GROW_20251120_MONTHLY = 'progrow20251120Monthly',
  PRO_GROW_20251120_YEARLY = 'progrow20251120Yearly',
  EDUCATION_NONPROFIT = 'educationNonprofit',
  ENTERPRISE = 'enterprise',
  ENTERPRISE_API_INTEGRATOR = 'enterpriseApiIntegrator',
  FREE = 'free',
  PRO_20200922_PAID = 'pro20200922Paid',
  PRO_20211112_PAID = 'pro20211112Paid',
  PRO_20250516_PAID = 'pro20250516Paid',
  PRO_20250818_PAID = 'pro20250818Paid',
  PRO_GROW_20261120_PAID = 'progrow20261120Paid',
  PRO_ADMIN_ASSIGNED = 'proAdminAssigned',
  PRO_EDUCATION_CHARITY = 'proEducationCharity',
  PRO_FREE_TEMPORARY = 'proFreeTemporary',
  PRO_PAID = 'proPaid',
  PRO_PAUSED = 'proPaused',
  PRO_TRIAL = 'proTrial',
  UNLIMITED_20211112_PAID = 'unlimited20211112Paid',
  UNLIMITED_ADMIN_ASSIGNED = 'unlimitedAdminAssigned',
}

export enum ProductTier {
  BASIC = 'basic',
  EDUCATION_NONPROFIT = 'educationNonprofit',
  ENTERPRISE = 'enterprise',
  FREE = 'free',
  PRO = 'pro',
  UNLIMITED = 'unlimited',
}

/**
 * Tiers that can be purchased by users in our apps
 */
export type PurchaseableTier =
  | ProductTier.BASIC
  | ProductTier.PRO
  | ProductTier.UNLIMITED;

export type PurchaseableSubTier = {
  [K in ProductSubTier as string]: K extends `${string}Paid` ? K : never;
}[string];

export type EnterpriseSubTier = {
  [K in ProductSubTier as string]: K extends `enterprise${string}` ? K : never;
}[string];

export type SubscriptionProduct = {
  /**
   * takes form of cuid
   */
  id: string;
  /**
   * Subtier of a product. Slightly more granular product
   */
  subtier: ProductSubTier;
  /**
   * High level tier level
   */
  tier: ProductTier;
};

export type UserSubscription = {
  /**
   * will the plan renew or cancel at the period end
   */
  autoRenewal?: boolean;
  /**
   * seconds since epoch when this cycle will end
   */
  currentPeriodEnd?: number;
  /**
   * seconds since epoch when this cycle started
   */
  currentPeriodStart?: number;
  /**
   * seconds since epoch when this plan started
   */
  currentPlanStart?: number;
  /**
   * list of features that change based on the user’s sub
   */
  feature: SubscriptionFeature;
  /**
   * flag indicating if this subscription was forcefully cancelled. this flow
   * currently occurs if the payment fails and we decide to turn off the subscription
   */
  forcedCancellationNotice?: boolean;
  /**
   * flag indicating if this subscription’s payment is past due
   */
  invoicePastDue?: boolean;
  /**
   * details about the subscription’s scheduled pause or active pause
   */
  pauseSchedule?: SubscriptionPauseSchedule;
  planLimit?: PlanLimit;
  planUsage?: PlanUsage;
  purchaseManagement?: PurchaseManagement;
  subscribedPlan: SubscriptionPlan;
  subscribedPlanAssociation?: PlanAssociation;
  subscribedProduct: SubscriptionProduct;
  userId: number;
};

export type CheckoutUrlPlanName =
  | SubscriptionPlanName.BASIC_20211112_MONTHLY
  | SubscriptionPlanName.BASIC_20211112_YEARLY
  | SubscriptionPlanName.BASIC_20250516_MONTHLY
  | SubscriptionPlanName.BASIC_20250516_YEARLY
  | SubscriptionPlanName.BASIC_20250818_MONTHLY
  | SubscriptionPlanName.BASIC_20250818_YEARLY
  | SubscriptionPlanName.PRO_20211112_MONTHLY
  | SubscriptionPlanName.PRO_20211112_YEARLY
  | SubscriptionPlanName.PRO_20250516_MONTHLY
  | SubscriptionPlanName.PRO_20250516_YEARLY
  | SubscriptionPlanName.PRO_20250818_MONTHLY
  | SubscriptionPlanName.PRO_20250818_YEARLY
  | SubscriptionPlanName.PRO_GROW_20251120_MONTHLY
  | SubscriptionPlanName.PRO_GROW_20251120_YEARLY
  | SubscriptionPlanName.PRO_MONTHLY
  | SubscriptionPlanName.PRO_YEARLY
  | SubscriptionPlanName.UNLIMITED_20211112_MONTHLY
  | TargetPlanAlias.PRO_GROW_MONTHLY_LATEST;

export type CheckoutUrlApplicationName =
  | 'discoDashboard'
  | 'discoPlay'
  | 'eddy'
  | 'makeHeadliner';

export interface CheckoutPostMessagePayload {
  data: {
    isSubscriptionPurchased: boolean;
  };
  source: typeof CHECKOUT_POSTMESSAGE_SOURCE;
}

export type PlanList = DataResponse<SubscriptionPlanDetail[]>;

export type PromotionCode = 'podcastPromoCredit';

export type SubscriptionUpdateRequest = {
  subscribeTo?: SubscribablePlanName;
  autoRenewal?: boolean;
  /**
   * update flag that indicates if the user’s previous subscription was forcefully
   * cancelled. This should be turned off if the user interacted with some notice
   * for being cancelled.
   */
  forcedCancellationNotice?: boolean;
  /**
   * seconds since epoch of the scheduled time for when the subscription should
   * be reverted to its original plan. intended to be used only with Pro Free
   * Temporary plan for now.
   */
  revertPlanAt?: number;
  /**
   * how many months the plan should be paused for. should be sent alone.
   */
  pausePlanDurationMonths?: number;
  /**
   * cancel a scheduled pause or cancel a currently active pause. should be sent alone
   */
  cancelPause?: boolean;
  /**
   * promotion code used when updating the subscription (upgrade to pro tier)
   */
  promotionCode?: PromotionCode;
  /**
   * rewardful referral id to be applied to the customer on checkout.
   */
  rewardfulReferral?: string | null;
};

export type CreatePaymentMethodRequest = {
  /**
   * token returned from stripe that represents a payment method identifier to
   * map to the user
   */
  stripeToken?: string;
  /**
   * payment id returned from payment intent. no validation occurs in the backend
   * for the payment id. in other words, a valid payment method id is expected to
   * be sent
   */
  paymentMethodId?: string;
};

export type CustomerSource = {
  /**
   * source id
   */
  id: string;
  /**
   * brand
   */
  brand: string;
  expMonth: string;
  expYear: string;
  /**
   * last 4 digits of the card
   */
  last4: string;
  /**
   * true if this source is the user’s default
   */
  isDefault: boolean;
};

export type CustomerDetail = {
  /**
   * customer id
   */
  id: string;
  /**
   * all sources tied to the user
   */
  sources?: CustomerSource[];
};

export type Customer = {
  customerId: string;
  customerDetail: CustomerDetail;
};

export type CouponDisplayHint = 'bogo';

export type Coupon = {
  /**
   * takes form of cuid
   */
  id: string;
  name: string;
  /**
   * hint to allow the frontend to conditionally change the display and message
   * to the user if this coupon is available.
   */
  couponDisplayHint?: CouponDisplayHint;
};

export type CouponsResponse = {
  coupons: Coupon[];
};

export type ReferralOwner = {
  id: number;
  referralKey: string;
  referralUrl: string;
};

export type ReferralEmailInvitation = {
  email: string;
};

export type ReferralEmailInvitationsRequest = {
  referralInvitations: ReferralEmailInvitation[];
};

export type PodcastPromoCreditsResponse = {
  remainingMilliCent: number;
  totalMilliCent: number;
};

export type PromotionType = 'proTrial' | 'coupon';

export type Promotion = {
  id: number;
  promotionKey: string;
  promotionUrl: string;
  rewardAmountDays: number;
  allowForExistingUsers: boolean;
  promotionType: PromotionType;
};
